/**
 * AtlasCraftingModule — the SWG-inspired crafting surface (F6), an SWG-style
 * WINDOW (drag / resize / roll-up / opacity, remembered per session):
 *
 *   SURVEY     every ACTIVE spawn with its 1–1000 stats and the LIVE
 *              concentration under your boots (the deterministic field —
 *              the client computes what the server pays); Sample button.
 *   INVENTORY  resource STACKS (one row per spawn, SWG stack law), crafted
 *              ITEMS (quality/ceiling, props, serial, crafter) with Use /
 *              Equip / Refine / List, your packed DEEDS, your SHIPS, and
 *              the SCHEMATICS you hold (the schematic wave, 2026-09-10) —
 *              F1 / the menu bar's Inventory button opens straight here.
 *   CRAFT      the schematic list; each slot auto-picks your best matching
 *              stack/component (cycle to override), assembly on Craft. A
 *              COMPLEX design (requiresSchematic) crafts only with its
 *              schematic in hand — the window says where to get one.
 *   RIGS       your extractor structures: tune to the best local spawn,
 *              collect the hopper.
 *   MARKET     the bazaar, versona stalls, and the SCHEMATIC DEALER of the
 *              nearest city or town (this season's board; Buy within 400 m).
 *
 * Resources exist ONLY on the commons — the window says so elsewhere.
 * Icons load from MinIO textures/icons/<key>.png (Higgsfield-generated),
 * falling back to a glyph when absent.
 */
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { ASSET_STORE_BASE } from '../config.js';
import {
  RES_FAMILIES, FAMILY_BY_KEY, RES_STATS, concentrationAt, lodeCenterOf, EXTRACTOR_SPECS, SAMPLE_MIN_CONC,
} from '../lib/resource_catalog.js';
import {
  SCHEMATICS, SCHEMATIC_BY_KEY, SHIP_SCHEMATICS, requiresSchematic, type Schematic,
} from '../lib/schematic_catalog.js';
import { isFurniture, FURNITURE } from '../lib/furniture_catalog.js';
import { isHovercraft } from '../lib/hovercraft_catalog.js';
import { furnishableStructureNear, worldToLocal } from '../lib/furniture_place.js';
import { structureSeats } from '../lib/structure_seat.js';
import { sfx } from '../lib/sfx.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';
import { nearestDealer, boardTurnsInS } from '../lib/schematic_dealer_lookup.js';

interface SpawnRowC { spawnId: bigint; familyKey: string; cls: string; name: string; statsJson: string; seed: number; active: boolean }
interface StackRowC { stackId: bigint; spawnId: bigint; familyKey: string; name: string; statsJson: string; qty: number }
interface ItemRowC { itemId: bigint; itemKey: string; name: string; quality: number; ceiling: number; propsJson: string; serial: string }
interface SchematicRowC { schemId: bigint; schematicKey: string; source: string }
interface ShipRowC { shipId: bigint; chassisKey: string; name: string; active: boolean }

const TABS = ['survey', 'inventory', 'craft', 'rigs', 'market'] as const;
type Tab = (typeof TABS)[number];

void RES_FAMILIES;

export class AtlasCraftingModule implements IModule {
  readonly name = 'AtlasCrafting';
  private win: UiWindow | null = null;
  private tabsEl: HTMLDivElement | null = null;
  private body: HTMLDivElement | null = null;
  private tab: Tab = 'survey';
  private refresh = 0;
  private tablesSubbed = false;
  /** Per-slot stack override (slotIndex → stackId hex) for the craft tab. */
  private slotPick = new Map<string, string>();
  private craftKey = SCHEMATICS[0].key;
  /** Survey heat-grid selection (spawnId hex). */
  private surveyPick: string | null = null;

  init(_app: App): void {
    void _app;
    window.addEventListener('keydown', (e) => {
      if (e.ctrlKey || e.metaKey) return;
      if (e.code !== 'F6' && e.code !== 'F1') return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (atlas.loadingGate || atlas.spaceFlight) return;
      e.preventDefault();
      // F1 = the INVENTORY (its own button on the menu bar); F6 = the bench.
      if (e.code === 'F1') this.openInventory();
      else this.toggle();
    });
    bus.on('atlas:world-cleared', () => this.close());
    bus.on('atlas:open-workbench', ({ tab, key }) => {
      if (tab) this.tab = tab;
      if (key && SCHEMATIC_BY_KEY[key]) { this.craftKey = key; this.tab = 'craft'; }
      this.open();
    });
    (window as unknown as Record<string, unknown>).__workbench = {
      open: (tab?: Tab, key?: string) => { if (tab) this.tab = tab; if (key) this.craftKey = key; this.open(); },
      close: () => this.close(),
      isOpen: () => this.isOpen(),
      tab: () => this.tab,
    };
  }

  isOpen(): boolean { return !!this.win && this.win.isOpen(); }
  private toggle(): void { if (this.isOpen()) this.close(); else this.open(); }

  /** Open straight onto the inventory (F1 / the Inventory menu button). */
  openInventory(): void {
    if (this.isOpen() && this.tab === 'inventory') { this.close(); return; }
    this.tab = 'inventory';
    this.open();
  }

  /** Open straight onto the bazaar tab (the market-terminal path). */
  openBazaar(): void {
    this.tab = 'market';
    this.open();
  }

  private close(): void {
    window.clearInterval(this.refresh);
    this.refresh = 0;
    this.win?.setOpen(false);
  }

  private ensureWindow(): UiWindow {
    if (this.win) return this.win;
    const win = makeWindow({
      id: 'workbench', title: 'Workbench', icon: 'build',
      width: 780, height: 580, minWidth: 540, minHeight: 320, x: Math.max(12, (window.innerWidth - 780) / 2), y: 56,
      onClose: () => { window.clearInterval(this.refresh); this.refresh = 0; },
    });
    win.root.id = 'crafting-window';
    const head = document.createElement('div');
    // Sticky: the window body is the scroller (ui_window re-applies its own
    // display on every drag), so the tab strip pins itself instead.
    head.style.cssText = 'display:flex;gap:8px;align-items:center;padding:8px 12px 6px;border-bottom:1px solid #22344c;' +
      'position:sticky;top:0;z-index:1;background:rgba(9,13,20,.98)';
    const tabs = document.createElement('div');
    tabs.style.cssText = 'display:flex;gap:4px;flex:1;flex-wrap:wrap';
    const guide = document.createElement('button');
    guide.textContent = 'GUIDE';
    guide.title = 'Crafting Guide — what you can craft right now and how (F10)';
    guide.style.cssText = 'cursor:pointer;background:#1c3350;color:#bfe0ff;border:1px solid #2f5a87;border-radius:6px;padding:3px 10px;font:600 10px ui-monospace,monospace';
    guide.addEventListener('click', () => bus.emit('atlas:open-craft-guide', { key: this.tab === 'craft' ? this.craftKey : undefined }));
    head.append(tabs, guide);
    const body = document.createElement('div');
    body.style.cssText = 'padding:10px 14px 14px;font:12px system-ui;color:#cfe0f5';
    win.body.append(head, body);
    this.win = win;
    this.tabsEl = tabs;
    this.body = body;
    return win;
  }

  private open(): void {
    const win = this.ensureWindow();
    win.setOpen(true);
    // Lazy subs: spawn + inventory tables replicate on first open (small).
    // (bug sweep) ONCE per session — every open AND every tab click (open2 =
    // close+open) re-subscribed all ten tables with no handle to release.
    if (!this.tablesSubbed && atlas.conn) try {
      this.tablesSubbed = true;
      atlas.conn.subscriptionBuilder().subscribe([
        'SELECT * FROM resource_spawn', 'SELECT * FROM player_resource',
        'SELECT * FROM player_item', 'SELECT * FROM extractor_state', 'SELECT * FROM structure',
        'SELECT * FROM resource_listing', 'SELECT * FROM player_equipment',
        'SELECT * FROM extractor_mod', 'SELECT * FROM player_vitals', 'SELECT * FROM settlement_econ',
        'SELECT * FROM player_schematic', 'SELECT * FROM player_ship', 'SELECT * FROM structure_deed',
      ]);
    } catch { /* fine */ }
    this.renderTabs();
    this.render();
    window.clearInterval(this.refresh);
    this.refresh = window.setInterval(() => this.render(), 2500);
  }

  /** Re-render after a tab switch (the header highlight + the body). */
  private open2(): void { this.renderTabs(); this.render(); }

  private renderTabs(): void {
    const tabs = this.tabsEl;
    if (!tabs) return;
    tabs.replaceChildren();
    for (const t of TABS) {
      const b = document.createElement('button');
      b.textContent = t.toUpperCase();
      b.dataset.tab = t;
      b.style.cssText = 'cursor:pointer;background:#141b28;color:#9fb4cc;border:1px solid #22344c;border-radius:6px;padding:3px 10px;font:600 10px ui-monospace,monospace';
      if (t === this.tab) { b.style.background = '#1d2b40'; b.style.color = '#cfe0f5'; }
      b.addEventListener('click', () => { this.tab = t; this.open2(); });
      tabs.appendChild(b);
    }
  }

  // ── data helpers ──────────────────────────────────────────────────────────
  private spawns(): SpawnRowC[] {
    const conn = atlas.conn;
    if (!conn) return [];
    try { return [...conn.db.resourceSpawn.iter()].filter((s) => s.active) as unknown as SpawnRowC[]; } catch { return []; }
  }

  private stacks(): StackRowC[] {
    const conn = atlas.conn;
    if (!conn?.identity) return [];
    try {
      return [...conn.db.playerResource.iter()].filter((r) => r.owner.isEqual(conn.identity!)) as unknown as StackRowC[];
    } catch { return []; }
  }

  private items(): ItemRowC[] {
    const conn = atlas.conn;
    if (!conn?.identity) return [];
    try {
      return [...conn.db.playerItem.iter()].filter((r) => r.owner.isEqual(conn.identity!)) as unknown as ItemRowC[];
    } catch { return []; }
  }

  private schematics(): SchematicRowC[] {
    const conn = atlas.conn;
    if (!conn?.identity) return [];
    try {
      return [...conn.db.playerSchematic.iter()].filter((r) => r.owner.isEqual(conn.identity!)) as unknown as SchematicRowC[];
    } catch { return []; }
  }

  private knows(key: string): boolean {
    return this.schematics().some((s) => s.schematicKey === key);
  }

  private ships(): ShipRowC[] {
    const conn = atlas.conn;
    if (!conn?.identity) return [];
    try {
      return [...conn.db.playerShip.iter()].filter((r) => r.owner.isEqual(conn.identity!)) as unknown as ShipRowC[];
    } catch { return []; }
  }

  private icon(key: string, glyph: string): HTMLElement {
    const wrap = document.createElement('span');
    wrap.style.cssText = 'display:inline-flex;width:26px;height:26px;align-items:center;justify-content:center;flex:0 0 auto;' +
      'background:#101a2a;border:1px solid #22344c;border-radius:6px;overflow:hidden;font-size:13px';
    const img = document.createElement('img');
    img.src = `${ASSET_STORE_BASE}/textures/icons/${key}.png`;
    img.style.cssText = 'width:100%;height:100%;object-fit:cover';
    img.onerror = () => { img.remove(); wrap.textContent = glyph; };
    wrap.appendChild(img);
    return wrap;
  }

  /** Two-line info cell built from TEXT NODES — names are data, never markup. */
  private infoCell(main: string, dim: string, sub: string, subColor = '#9fb4cc'): HTMLDivElement {
    const info = document.createElement('div');
    info.style.cssText = 'flex:1;min-width:0';
    const top = document.createElement('div');
    const b = document.createElement('b');
    b.textContent = main;
    top.appendChild(b);
    if (dim) {
      const d = document.createElement('span');
      d.textContent = ` ${dim}`;
      d.style.color = '#7f93ad';
      top.appendChild(d);
    }
    const bottom = document.createElement('div');
    bottom.textContent = sub;
    bottom.style.cssText = `color:${subColor};font:10px ui-monospace,monospace`;
    info.append(top, bottom);
    return info;
  }

  private statLine(statsJson: string): string {
    try {
      const st = JSON.parse(statsJson) as Record<string, number>;
      return RES_STATS.filter((k) => (st[k] ?? 0) > 0).map((k) => `${k} ${st[k]}`).join(' · ');
    } catch { return ''; }
  }

  private note(msg: string, err = false, sound?: string): void {
    // Every note is audible: errors buzz, successes chime — a caller with a
    // semantically richer cue (coins, harvest, assembly) passes its own key.
    sfx(sound ?? (err ? 'sfx_error' : 'sfx_notify'), { gain: err ? 0.25 : 0.3, cooldownMs: 400 });
    const el = document.createElement('div');
    el.textContent = msg;
    el.className = 'craft-note';
    el.style.cssText = `margin-top:6px;color:${err ? '#ff9d9d' : '#9fe3c0'};font:600 11px ui-monospace,monospace`;
    this.body?.appendChild(el);
    window.setTimeout(() => el.remove(), 3500);
  }

  private section(body: HTMLElement, label: string, top = 2): void {
    const h = document.createElement('div');
    h.textContent = label;
    h.style.cssText = `font:700 10px ui-monospace,monospace;color:#6f8cae;margin:${top}px 0 6px`;
    body.appendChild(h);
  }

  // ── render ────────────────────────────────────────────────────────────────
  private render(): void {
    const body = this.body;
    if (!body || !this.isOpen()) return;
    body.replaceChildren();
    if (this.tab === 'survey') this.renderSurvey(body);
    else if (this.tab === 'inventory') this.renderInventory(body);
    else if (this.tab === 'craft') this.renderCraft(body);
    else if (this.tab === 'rigs') this.renderRigs(body);
    else this.renderMarket(body);
  }

  private row(): HTMLDivElement {
    const r = document.createElement('div');
    r.style.cssText = 'display:flex;gap:8px;align-items:center;background:rgba(16,26,42,.7);' +
      'border:1px solid #22344c;border-radius:8px;padding:6px 10px;margin-bottom:4px;flex-wrap:wrap';
    return r;
  }

  /** Unsettled counter attempts by listing — a retry REUSES the request id, so the
   *  server's receipt makes a lost response buy-once (lib/merchant_ops, the review's slice). */
  private readonly pendingSettle = new Map<string, string>();

  /**
   * The counter protocol. Within trading range: approach (the counter or a place
   * in line) → hold one → settle with a request id. Out of range: the terminal's
   * direct buy. A pending request id is checked against my receipts FIRST — the
   * previous attempt may have landed while its response was lost.
   */
  private async buyAtCounter(conn: NonNullable<typeof atlas.conn>, l: { listingId: bigint; versonaId: bigint; name: string }): Promise<void> {
    const me = conn.identity;
    if (!me) return;
    const key = String(l.listingId);
    const pending = this.pendingSettle.get(key);
    if (pending) {
      for (const rc of conn.db.merchantReceipt.iter()) {
        if (rc.requestId === pending && rc.identity.isEqual(me)) {
          this.pendingSettle.delete(key);
          this.note(`Bought ${l.name} — that purchase had already gone through`, false, 'sfx_coin_purchase');
          return;
        }
      }
    }
    const hot = conn.db.versonaState.versonaId.find(l.versonaId);
    const p = conn.db.playerState.identity.find(me);
    const near = !!hot && !!p && Math.hypot(hot.curX - p.posX, hot.curZ - p.posZ) <= 14;
    try {
      if (!near) {
        await conn.reducers.buyVersonaListing({ listingId: l.listingId, qty: 1 });
        this.note(`Bought ${l.name} through the terminal — the merchant pockets the credits`, false, 'sfx_coin_purchase');
        return;
      }
      await conn.reducers.approachMerchant({ versonaId: l.versonaId });
      const svc = conn.db.merchantService.versonaId.find(l.versonaId);
      if (!svc || svc.phase === 'idle' || !svc.customer.isEqual(me)) {
        const waiting = [...conn.db.merchantQueue.iter()].filter((q) => q.versonaId === l.versonaId).length;
        this.note(`The merchant is serving another customer — you are in line (${waiting} waiting)`, true);
        return;
      }
      await conn.reducers.holdMerchantListing({ listingId: l.listingId, qty: 1 });
      const hold = [...conn.db.merchantHold.iter()].find((h) => h.listingId === l.listingId && h.identity.isEqual(me));
      if (!hold) { this.note('The goods could not be held', true); return; }
      const requestId = pending ?? `buy-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      this.pendingSettle.set(key, requestId);
      await conn.reducers.settleMerchantHold({ holdId: hold.holdId, listingId: l.listingId, qty: 1, requestId });
      this.pendingSettle.delete(key);
      this.note(`Bought ${l.name} — the merchant hands it over`, false, 'sfx_coin_purchase');
    } catch (e) {
      this.note((e as Error).message, true);
    }
  }

  private btn(label: string, fn: () => void, danger = false): HTMLButtonElement {
    const b = document.createElement('button');
    b.textContent = label;
    b.style.cssText = danger
      ? 'cursor:pointer;background:#3a1d22;color:#ffb3b3;border:1px solid #6b2f38;border-radius:6px;padding:3px 10px;font:600 11px system-ui'
      : 'cursor:pointer;background:#1d2635;color:#9cf;border:1px solid #2a3f5a;border-radius:6px;padding:3px 10px;font:600 11px system-ui';
    b.addEventListener('click', fn);
    return b;
  }

  private renderSurvey(body: HTMLElement): void {
    const cap = document.createElement('div');
    cap.textContent = 'Active spawns rotate every few days. Concentration is measured where you STAND — walk to raise it. Resources are gathered on the Commons only.';
    cap.style.cssText = 'color:#7f93ad;margin-bottom:8px';
    body.appendChild(cap);
    const px = atlas.localX, pz = atlas.localZ;
    const rows = this.spawns()
      .map((sp) => ({ sp, conc: concentrationAt(sp.seed, px, pz) }))
      .sort((a, b) => b.conc - a.conc);
    // The SWG survey scanner: an 11×11 local heat grid for the SELECTED
    // spawn, plus the LODE bearing (the vein lives somewhere — go there).
    const picked = rows.find(({ sp }) => String(sp.spawnId) === this.surveyPick) ?? rows[0];
    if (picked) {
      const grid = document.createElement('canvas');
      grid.width = grid.height = 165;
      grid.style.cssText = 'width:165px;height:165px;border:1px solid #22344c;border-radius:8px;float:right;margin:0 0 8px 8px';
      const g = grid.getContext('2d')!;
      const STEP = 900; // meters per cell — a walkable scan
      for (let gy = 0; gy < 11; gy++) {
        for (let gx = 0; gx < 11; gx++) {
          const c = concentrationAt(picked.sp.seed, px + (gx - 5) * STEP, pz + (gy - 5) * STEP);
          const t2 = Math.min(1, c * 1.6);
          g.fillStyle = `rgb(${Math.round(16 + 40 * t2)}, ${Math.round(26 + 200 * t2)}, ${Math.round(42 + 130 * t2)})`;
          g.fillRect(gx * 15, gy * 15, 14, 14);
        }
      }
      g.strokeStyle = '#fff';
      g.strokeRect(5 * 15, 5 * 15, 14, 14); // you
      const lode = lodeCenterOf(picked.sp.seed);
      const ang = Math.atan2(lode.z - pz, lode.x - px);
      g.strokeStyle = '#ffd97e';
      g.beginPath();
      g.moveTo(82, 82);
      g.lineTo(82 + Math.cos(ang) * 70, 82 + Math.sin(ang) * 70);
      g.stroke();
      body.appendChild(grid);
      const lodeD = Math.hypot(lode.x - px, lode.z - pz);
      const lodeLine = document.createElement('div');
      lodeLine.textContent = `${picked.sp.name}: the lode runs ${lodeD >= 1000 ? `${Math.round(lodeD / 1000)} km` : `${Math.round(lodeD)} m`} out (gold bearing on the scanner)`;
      lodeLine.style.cssText = 'color:#ffd97e;font:600 11px ui-monospace,monospace;margin-bottom:6px';
      body.appendChild(lodeLine);
    }
    for (const { sp, conc } of rows) {
      const r = this.row();
      if (String(sp.spawnId) === String(picked?.sp.spawnId)) r.style.borderColor = '#ffd97e';
      r.style.cursor = 'pointer';
      r.addEventListener('click', () => { this.surveyPick = String(sp.spawnId); this.render(); });
      r.appendChild(this.icon(sp.familyKey, '⛏'));
      const fam = FAMILY_BY_KEY[sp.familyKey];
      const info = this.infoCell(sp.name, `(${fam?.label ?? sp.familyKey})`, this.statLine(sp.statsJson));
      const concEl = document.createElement('div');
      const pct = Math.round(conc * 100);
      concEl.textContent = `${pct}%`;
      concEl.style.cssText = `width:44px;text-align:right;font:700 12px ui-monospace,monospace;color:${conc >= SAMPLE_MIN_CONC ? '#9fe3c0' : '#66788f'}`;
      r.append(info, concEl);
      if (conc >= SAMPLE_MIN_CONC) {
        r.appendChild(this.btn('Sample', () => {
          atlas.conn?.reducers.sampleResource({ spawnId: sp.spawnId })
            .then(() => this.note(`Sampled ${sp.name}`))
            .catch((e: Error) => this.note(e.message, true));
        }));
      }
      body.appendChild(r);
    }
    if (rows.length === 0) {
      const empty = document.createElement('div');
      empty.textContent = 'No spawns replicated yet.';
      body.appendChild(empty);
    }
  }

  private renderInventory(body: HTMLElement): void {
    const conn = atlas.conn;
    // ── Packed structure deeds (rigs, houses) — the SWG holdings, surfaced
    //    HERE because "where is my rig?" is answered by the inventory first.
    //    Place enters the footprint ghost (the Structures tab's own flow).
    const deeds = conn?.identity
      ? [...conn.db.structureDeed.iter()].filter((d) => d.owner.isEqual(conn.identity!) && d.packed)
      : [];
    if (deeds.length > 0) {
      this.section(body, 'DEEDS — packed structures, place them on open ground');
      for (const d of deeds) {
        const r = this.row();
        r.appendChild(this.icon(d.structureType, '📜'));
        const label = SCHEMATIC_BY_KEY[d.structureType]?.name ?? d.structureType.replace(/_/g, ' ');
        r.appendChild(this.infoCell(label, '', `${d.lotsRequired} lot${d.lotsRequired === 1 ? '' : 's'} · ${d.baseMaintPerHour}/h upkeep · ${d.storedMaint} maint banked`));
        r.appendChild(this.btn('Place', () => {
          bus.emit('atlas:structure-place', { deedId: d.deedId.toString(), structureType: d.structureType });
          this.close();
        }));
        body.appendChild(r);
      }
    }
    // ── Ships in the hangar (the schematic wave: a hull can be taken apart
    //    for its design — the ship is destroyed, the schematic learned).
    const ships = this.ships();
    if (ships.length > 0) {
      this.section(body, 'SHIPS — your hangar (fit them at a station; P near one)', 8);
      for (const sh of ships) {
        const r = this.row();
        r.className = 'inv-ship';
        r.appendChild(this.icon(sh.chassisKey, '🚀'));
        const sch = SCHEMATIC_BY_KEY[sh.chassisKey];
        const known = sch ? this.knows(sch.key) : true;
        r.appendChild(this.infoCell(sh.name, sh.active ? '(active)' : '', `${sh.chassisKey.replace('ship_', '')} hull · ship #${sh.shipId}${known ? ' · design known' : ''}`));
        if (sch && !known) {
          r.appendChild(this.btn('Reverse engineer', () => {
            if (!window.confirm(`Break up the ${sh.name} to learn the ${sch.name} schematic?\n\nTHE SHIP IS DESTROYED. Fitted components come off first and return to your inventory.`)) return;
            conn?.reducers.reverseEngineerShip({ shipId: sh.shipId })
              .then(() => this.note(`${sch.name} schematic learned — the hull is gone`, false, 'sfx_craft_success'))
              .catch((e: Error) => this.note(e.message, true));
          }, true));
        }
        body.appendChild(r);
      }
    }
    // ── Equipped gear ──
    const equipped = conn?.identity
      ? [...conn.db.playerEquipment.iter()].filter((e) => e.owner.isEqual(conn.identity!))
      : [];
    if (equipped.length > 0) {
      this.section(body, 'EQUIPPED', 8);
      for (const eq of equipped) {
        const r = this.row();
        r.appendChild(this.icon(eq.itemKey, '🛠'));
        r.appendChild(this.infoCell(`${eq.name}`, `(${eq.slot})`, `q ${eq.quality} · ${eq.serial}`));
        r.appendChild(this.btn('Unequip', () => {
          conn?.reducers.unequipItem({ slot: eq.slot })
            .then(() => this.note(`${eq.name} stowed`, false, 'sfx_equip'))
            .catch((e: Error) => this.note(e.message, true));
        }));
        body.appendChild(r);
      }
    }
    // ── Known designs ──
    const known = this.schematics();
    this.section(body, `SCHEMATICS — designs you hold (${known.length}); basic work needs none`, 8);
    if (known.length === 0) {
      const none = document.createElement('div');
      none.textContent = 'No complex designs yet. Buy one from a schematic dealer in any city or town (Market tab), or reverse-engineer a finished piece.';
      none.style.cssText = 'color:#55677f;margin-bottom:6px';
      body.appendChild(none);
    }
    for (const sk of known.sort((a, b) => a.schematicKey.localeCompare(b.schematicKey))) {
      const sch = SCHEMATIC_BY_KEY[sk.schematicKey];
      const r = this.row();
      r.className = 'inv-schematic';
      r.appendChild(this.icon(sk.schematicKey, '📐'));
      r.appendChild(this.infoCell(`${sch?.name ?? sk.schematicKey} schematic`, '', `${sk.source === 'reverse' ? 'reverse-engineered' : sk.source === 'bought' ? 'bought from a dealer' : sk.source} · complexity ${sch?.complexity ?? '?'}`));
      r.appendChild(this.btn('Craft', () => { this.craftKey = sk.schematicKey; this.tab = 'craft'; this.open2(); }));
      body.appendChild(r);
    }
    this.section(body, 'RESOURCES', 8);
    for (const st of this.stacks().sort((a, b) => b.qty - a.qty)) {
      const r = this.row();
      r.appendChild(this.icon(st.familyKey, '▦'));
      r.appendChild(this.infoCell(`${st.name} × ${st.qty}`, '', this.statLine(st.statsJson)));
      r.appendChild(this.btn('Sell→town', () => {
        const conn2 = atlas.conn;
        if (!conn2) return;
        // Nearest settlement square (within 400 m server-side).
        const econs = [...conn2.db.settlementEcon.iter()]
          .sort((a, b) => Math.hypot(a.centerX - atlas.localX, a.centerZ - atlas.localZ) - Math.hypot(b.centerX - atlas.localX, b.centerZ - atlas.localZ));
        if (!econs[0]) { this.note('No settlement replicated', true); return; }
        const qty = Math.min(st.qty, 20);
        conn2.reducers.sellResourceToTown({ stackId: st.stackId, qty, settlementId: econs[0].settlementId })
          .then(() => this.note(`Sold ${qty} to the town stores (+standing)`, false, 'sfx_coin_sell'))
          .catch((e: Error) => this.note(e.message, true));
      }));
      r.appendChild(this.btn('List', () => {
        const price = Number(window.prompt(`List how many SOV for ${Math.min(st.qty, 50)} × ${st.name}?`, '20') ?? 0);
        if (!price) return;
        atlas.conn?.reducers.listResource({ stackId: st.stackId, qty: Math.min(st.qty, 50), priceSov: BigInt(price) })
          .then(() => this.note('Listed on the bazaar'))
          .catch((e: Error) => this.note(e.message, true));
      }));
      body.appendChild(r);
    }
    this.section(body, 'ITEMS', 10);
    const items = this.items().sort((a, b) => Number(b.itemId - a.itemId));
    if (items.length === 0) {
      const none = document.createElement('div');
      none.textContent = 'Nothing crafted yet — the Craft tab lists what you can make from your resources.';
      none.style.cssText = 'color:#55677f;margin-bottom:6px';
      body.appendChild(none);
    }
    for (const it of items) {
      const r = this.row();
      r.className = 'inv-item-row';
      r.appendChild(this.icon(it.itemKey, '⚒'));
      let props = '';
      try {
        props = Object.entries(JSON.parse(it.propsJson) as Record<string, number>)
          .map(([k, v]) => `${k.replace(/_/g, ' ')} ${v}`).join(' · ');
      } catch { /* none */ }
      r.appendChild(this.infoCell(it.name, it.serial, `q ${it.quality}/${it.ceiling} · ${props}`));
      // Furnishing wave: crafted furniture places into a structure the player
      // owns/admins (stand at it) — the piece leaves the inventory and stands
      // in the world; F near it picks it back up. Unowned city/town buildings
      // have no structure row, so they can never be furnished.
      if (isFurniture(it.itemKey)) {
        r.appendChild(this.btn('Place here', () => {
          const conn = atlas.conn;
          if (!conn?.identity || atlas.currentWorldId === null) return;
          const s = furnishableStructureNear(conn, conn.identity, atlas.currentWorldId, atlas.localX, atlas.localZ);
          if (!s) { this.note('Stand at a structure you own (or admin) to place furniture', true); return; }
          const { lx, lz } = worldToLocal(s, atlas.localX, atlas.localZ);
          // Drop at the feet, facing the player's way; the structure seat is
          // the local floor origin, so ly derives from the capsule base (≈1 m
          // below localY). Seat unknown → ground floor (ly 0).
          const psy = structureSeats.get(s.structureId.toString())?.y;
          const ly = Math.max(0, psy !== undefined ? atlas.localY - 1.0 - psy : 0);
          conn.reducers.placeFurniture({ playerItemId: it.itemId, structureId: s.structureId, lx, ly, lz, yaw: atlas.localYaw })
            .then(() => this.note(`${FURNITURE[it.itemKey]?.name ?? it.name} placed — F near it to pick it up`, false, 'sfx_furniture_place'))
            .catch((e: Error) => this.note(e.message, true));
        }));
      }
      // Droid wave: a crafted droid deploys onto the Commons as a persistent
      // unit (walk to it and press K for its terminal; recall from there).
      if (it.itemKey.startsWith('droid_')) {
        r.appendChild(this.btn('Deploy', () => {
          const conn = atlas.conn;
          if (!conn) return;
          conn.reducers.deployDroid({ itemId: it.itemId })
            .then(() => { this.note(`${it.name} deployed at your feet — K near it for its terminal`, false, 'sfx_droid_activate'); this.open2(); })
            .catch((e: Error) => this.note(e.message, true));
        }));
      }
      // Vehicle wave: a crafted hovercraft mounts straight from the inventory
      // (AtlasVehicleModule owns the ride; X in the open works too).
      if (isHovercraft(it.itemKey)) {
        r.appendChild(this.btn('Ride', () => {
          const v = (window as unknown as Record<string, { mount?: (id: string) => void }>).__vehicle;
          v?.mount?.(it.itemId.toString());
          this.close();
        }));
      }
      const sch2 = SCHEMATIC_BY_KEY[it.itemKey];
      const isWeapon = !!sch2 && sch2.props.includes('damage');
      const usable = ['field_tool', 'travelers_pack', 'trail_rations', 'medic_salve', 'signal_beacon', 'survival_kit'].includes(it.itemKey) || isWeapon;
      if (usable) {
        const gear = it.itemKey === 'field_tool' || it.itemKey === 'travelers_pack' || isWeapon;
        r.appendChild(this.btn(gear ? 'Equip' : 'Use', () => {
          atlas.conn?.reducers.useItem({ itemId: it.itemId })
            .then(() => this.note(gear ? `${it.name} equipped` : `${it.name} used`, false, gear ? 'sfx_equip' : 'sfx_heal_use'))
            .catch((e: Error) => this.note(e.message, true));
        }));
      }
      if (it.quality < it.ceiling) {
        const props2 = sch2?.props ?? [];
        // Per-prop experimentation: one button per property + a spread pass.
        for (const prop of props2.length > 1 ? props2 : ['']) {
          r.appendChild(this.btn(prop ? `Refine ${prop.replace(/_/g, ' ')}` : 'Refine', () => {
            const stack = this.stacks().find((st2) => sch2?.slots.some((sl) =>
              (sl.accepts.startsWith('family:') && sl.accepts.slice(7) === st2.familyKey)
              || (sl.accepts.startsWith('class:') && FAMILY_BY_KEY[st2.familyKey]?.cls === sl.accepts.slice(6))));
            if (!stack) { this.note('No matching resources to refine with', true); return; }
            atlas.conn?.reducers.refineItem({ itemId: it.itemId, stackId: stack.stackId, prop })
              .then(() => this.note(`Refined ${it.name}`))
              .catch((e: Error) => this.note(e.message, true));
          }));
        }
      }
      r.appendChild(this.btn('List', () => {
        const price = Number(window.prompt(`List ${it.name} for how many SOV?`, '50') ?? 0);
        if (!price) return;
        atlas.conn?.reducers.listItem({ itemId: it.itemId, priceSov: BigInt(price) })
          .then(() => this.note('Listed on the bazaar'))
          .catch((e: Error) => this.note(e.message, true));
      }));
      // The schematic wave: a COMPLEX piece can be taken apart for its design
      // (the item is destroyed). A design already held is refused server-side
      // — the button only shows while it would teach something.
      if (sch2 && requiresSchematic(sch2) && !this.knows(sch2.key)) {
        r.appendChild(this.btn('Reverse engineer', () => {
          if (!window.confirm(`Take the ${it.name} apart to learn the ${sch2.name} schematic?\n\nTHE ITEM IS DESTROYED.`)) return;
          atlas.conn?.reducers.reverseEngineerItem({ itemId: it.itemId })
            .then(() => this.note(`${sch2.name} schematic learned — the piece is gone`, false, 'sfx_craft_success'))
            .catch((e: Error) => this.note(e.message, true));
        }, true));
      }
      body.appendChild(r);
    }
  }

  /** Best stack for a slot (highest weighted quality); respects overrides. */
  private pickStack(sch: Schematic, slotIdx: number): StackRowC | null {
    const slot = sch.slots[slotIdx];
    const key = `${sch.key}:${slotIdx}`;
    const candidates = this.stacks().filter((st) => {
      if (st.qty < slot.qty) return false;
      if (slot.accepts.startsWith('family:')) return slot.accepts.slice(7) === st.familyKey;
      if (slot.accepts.startsWith('class:')) return FAMILY_BY_KEY[st.familyKey]?.cls === slot.accepts.slice(6);
      return false;
    });
    if (candidates.length === 0) return null;
    const over = this.slotPick.get(key);
    if (over) {
      const found = candidates.find((c) => String(c.stackId) === over);
      if (found) return found;
    }
    const score = (st: StackRowC): number => {
      try {
        const stats = JSON.parse(st.statsJson) as Record<string, number>;
        let n = 0, d = 0;
        for (const [k, w] of Object.entries(slot.weights)) { n += (stats[k] ?? 0) * (w ?? 0); d += w ?? 0; }
        return d > 0 ? n / d : 0;
      } catch { return 0; }
    };
    return candidates.sort((a, b) => score(b) - score(a))[0];
  }

  private renderCraft(body: HTMLElement): void {
    const sel = document.createElement('select');
    sel.id = 'craft-select';
    sel.style.cssText = 'background:#141b28;color:#cfe0f5;border:1px solid #22344c;border-radius:6px;padding:4px 8px;margin-bottom:8px;max-width:100%';
    for (const sch of SCHEMATICS) {
      const o = document.createElement('option');
      o.value = sch.key;
      const gated = requiresSchematic(sch);
      const tag = sch.kind === 'component' ? ' (component)' : gated ? (this.knows(sch.key) ? ' [schematic held]' : ' [needs schematic]') : '';
      o.textContent = `${sch.name}${tag}`;
      if (sch.key === this.craftKey) o.selected = true;
      sel.appendChild(o);
    }
    sel.addEventListener('change', () => { this.craftKey = sel.value; this.render(); });
    body.appendChild(sel);
    const sch = SCHEMATIC_BY_KEY[this.craftKey];
    if (!sch) return;
    const gated = requiresSchematic(sch);
    const known = !gated || this.knows(sch.key);
    if (gated) {
      const gate = document.createElement('div');
      gate.id = 'craft-gate';
      gate.textContent = known
        ? `You hold the ${sch.name} schematic.`
        : `SCHEMATIC REQUIRED — a ${sch.name} is a complex design. Buy it from a schematic dealer in any city or town (Market tab) or reverse-engineer a finished ${sch.name} (Inventory tab; the piece is destroyed).`;
      gate.style.cssText = `margin-bottom:8px;padding:6px 10px;border-radius:8px;border:1px solid ${known ? '#2f5a3c' : '#6b4a2f'};background:${known ? 'rgba(20,50,32,.5)' : 'rgba(60,40,20,.5)'};color:${known ? '#9fe3c0' : '#ffd0a0'}`;
      body.appendChild(gate);
    }
    if (sch.complexity >= 3) {
      const ws = document.createElement('div');
      ws.textContent = 'Workshop work: craft within 150 m of a deployed structure or 300 m of a town center.';
      ws.style.cssText = 'color:#7f93ad;margin-bottom:8px;font:10px ui-monospace,monospace';
      body.appendChild(ws);
    }
    const myItems = this.items();
    const chosenStacks: bigint[] = [];
    const chosenComponents: bigint[] = [];
    let ready = true;
    const usedComponentIds = new Set<string>();
    sch.slots.forEach((slot, i) => {
      const r = this.row();
      let info: HTMLDivElement;
      if (slot.accepts.startsWith('component:')) {
        const want = slot.accepts.slice(10);
        // A component item feeds ONE slot — two slots wanting the same key
        // must draw distinct rows (the server deletes each consumed id).
        const have = myItems.filter((it) => it.itemKey === want && !usedComponentIds.has(String(it.itemId))).slice(0, slot.qty);
        const okSlot = have.length >= slot.qty;
        info = this.infoCell(
          `${slot.name} — needs ${slot.qty} × ${SCHEMATIC_BY_KEY[want]?.name ?? want}`, '',
          `${have.length}/${slot.qty} ready${have.length ? ` (q ${have.map((h) => h.quality).join(', ')})` : ''}`,
          okSlot ? '#9fe3c0' : '#ff9d9d',
        );
        if (okSlot) for (const h of have) { chosenComponents.push(h.itemId); usedComponentIds.add(String(h.itemId)); }
        else ready = false;
        if (!okSlot) {
          r.appendChild(this.btn(`Make ${SCHEMATIC_BY_KEY[want]?.name ?? want}`, () => { this.craftKey = want; this.render(); }));
        }
      } else {
        const pick = this.pickStack(sch, i);
        info = this.infoCell(
          `${slot.name} — ${slot.qty} × ${slot.accepts.replace('family:', '').replace('class:', 'any ').replace(/_/g, ' ')}`, '',
          pick ? `${pick.name} (${pick.qty} held) · ${this.statLine(pick.statsJson)}` : 'no matching stack — survey & sample it, or buy from the bazaar',
          pick ? '#9fe3c0' : '#ff9d9d',
        );
        if (pick) chosenStacks.push(pick.stackId);
        else ready = false;
      }
      r.insertBefore(info, r.firstChild);
      if (!slot.accepts.startsWith('component:')) {
        r.style.cursor = 'pointer';
        r.title = 'Click to cycle to another matching stack';
        r.addEventListener('click', () => {
          const key = `${sch.key}:${i}`;
          const candidates = this.stacks().filter((st) => {
            if (st.qty < slot.qty) return false;
            if (slot.accepts.startsWith('family:')) return slot.accepts.slice(7) === st.familyKey;
            if (slot.accepts.startsWith('class:')) return FAMILY_BY_KEY[st.familyKey]?.cls === slot.accepts.slice(6);
            return false;
          });
          if (candidates.length < 2) return;
          const cur = this.slotPick.get(key);
          const idx = candidates.findIndex((c) => String(c.stackId) === cur);
          this.slotPick.set(key, String(candidates[(idx + 1) % candidates.length].stackId));
          this.render();
        });
      }
      body.appendChild(r);
    });
    const craft = this.btn(`Craft ${sch.name}`, () => {
      // A botched assembly COMMITS (half-refund) and resolves without an
      // item — success means a NEW row of this schematic appeared (count
      // comparison lies when the craft consumed component items). A ship
      // hull lands in the HANGAR instead (a new playerShip row).
      const isShip = !!SHIP_SCHEMATICS[sch.key];
      const before = isShip
        ? new Set(this.ships().map((s) => String(s.shipId)))
        : new Set(this.items().filter((i) => i.itemKey === sch.key).map((i) => String(i.itemId)));
      sfx('sfx_craft_start', { gain: 0.35, cooldownMs: 500 }); // the bench spools up
      atlas.conn?.reducers.craftItem({ schematicKey: sch.key, resourceStackIds: chosenStacks, componentItemIds: chosenComponents })
        .then(() => window.setTimeout(() => {
          const minted = isShip
            ? this.ships().some((s) => !before.has(String(s.shipId)))
            : this.items().some((i) => i.itemKey === sch.key && !before.has(String(i.itemId)));
          if (minted) this.note(isShip ? `${sch.name} laid down — it waits in your hangar` : `${sch.name} assembled`, false, 'sfx_craft_success');
          else this.note(`The assembly FAILED — half the materials were saved`, true);
        }, 600))
        .catch((e: Error) => this.note(e.message, true));
    });
    craft.id = 'craft-go';
    if (!ready || !known) { craft.disabled = true; craft.style.opacity = '0.4'; }
    body.appendChild(craft);
  }

  private renderRigs(body: HTMLElement): void {
    const conn = atlas.conn;
    if (!conn?.identity) return;
    const mine = [...conn.db.structure.iter()]
      .filter((s) => s.owner.isEqual(conn.identity!) && s.structureType.startsWith('extractor_'));
    if (mine.length === 0) {
      const empty = document.createElement('div');
      empty.textContent = 'No extraction rigs deployed. Your starter Field Extractor Rig is a packed DEED in the Inventory tab — Place it over a hotspot (survey first!). More rigs are crafted on the Craft tab.';
      body.appendChild(empty);
      return;
    }
    const spawns = this.spawns();
    for (const st of mine) {
      const spec = EXTRACTOR_SPECS[st.structureType];
      let ext: { targetSpawnId: bigint; storedQty: number; concentration: number } | null = null;
      try { ext = (conn.db as never as { extractorState: { structureId: { find(id: bigint): typeof ext } } }).extractorState.structureId.find(st.structureId); } catch { /* n/a */ }
      const r = this.row();
      r.appendChild(this.icon(st.structureType, '🏭'));
      const tuned = ext && ext.targetSpawnId !== 0n ? spawns.find((sp) => sp.spawnId === ext!.targetSpawnId) : null;
      r.appendChild(this.infoCell(
        `${st.structureType.replace(/_/g, ' ')} #${st.structureId}`, '',
        `${tuned ? `tuned: ${tuned.name} @ ${Math.round((ext?.concentration ?? 0) * 100)}%` : 'untuned'}`
        + ` · hopper ${Math.floor(ext?.storedQty ?? 0)}/${spec?.capacity ?? '?'} · ${spec?.ratePerH ?? '?'}/h base`,
      ));
      // Tune to the best LOCAL spawn (concentration at the rig).
      const best = spawns
        .map((sp) => ({ sp, conc: concentrationAt(sp.seed, st.posX, st.posZ) }))
        .sort((a, b) => b.conc - a.conc)[0];
      if (best && best.conc >= SAMPLE_MIN_CONC) {
        r.appendChild(this.btn(`Tune → ${best.sp.name} (${Math.round(best.conc * 100)}%)`, () => {
          conn.reducers.tuneExtractor({ structureId: st.structureId, spawnId: best.sp.spawnId })
            .then(() => this.note('Rig tuned'))
            .catch((e: Error) => this.note(e.message, true));
        }));
      }
      r.appendChild(this.btn('Collect', () => {
        conn.reducers.collectExtractor({ structureId: st.structureId })
          .then(() => this.note('Hopper emptied into your hold', false, 'sfx_harvest'))
          .catch((e: Error) => this.note(e.message, true));
      }));
      // A held Extraction Rig Core can be seated (consumed; +50% rate at q1000).
      const core = this.items().find((i) => i.itemKey === 'extraction_rig_core');
      let mod: { coreQuality: number } | null = null;
      try { mod = (conn.db as never as { extractorMod: { structureId: { find(id: bigint): typeof mod } } }).extractorMod.structureId.find(st.structureId); } catch { /* n/a */ }
      if (mod) {
        const badge = document.createElement('span');
        badge.textContent = `core q${mod.coreQuality}`;
        badge.style.cssText = 'color:#ffd97e;font:600 10px ui-monospace,monospace';
        r.appendChild(badge);
      }
      if (core) {
        r.appendChild(this.btn(`Slot core (q${core.quality})`, () => {
          conn.reducers.slotRigCore({ structureId: st.structureId, itemId: core.itemId })
            .then(() => this.note('Rig core seated — the drills sing'))
            .catch((e: Error) => this.note(e.message, true));
        }));
      }
      body.appendChild(r);
    }
  }

  private renderMarket(body: HTMLElement): void {
    const conn = atlas.conn;
    if (!conn?.identity) return;
    const me = conn.identity;
    const listings = [...conn.db.resourceListing.iter()].filter((l) => l.active);
    const mine = listings.filter((l) => l.seller.isEqual(me));
    const others = listings.filter((l) => !l.seller.isEqual(me));
    const wallet = conn.db.wallet?.identity?.find?.(me);
    const cap = document.createElement('div');
    cap.textContent = `The bazaar — escrowed listings, 5% tax to the platform. Your purse: ${wallet ? `${wallet.sov} ⓒ` : '…'}`;
    cap.style.cssText = 'color:#7f93ad;margin-bottom:8px';
    body.appendChild(cap);
    // ── The schematic dealer of the nearest city / town (the schematic wave) ──
    this.section(body, 'SCHEMATIC DEALER — complex designs, sold by the season');
    {
      const view = nearestDealer(conn, atlas.localX, atlas.localZ, Date.now());
      const box = document.createElement('div');
      box.id = 'dealer-board';
      if (!view) {
        box.textContent = 'No settlement charted yet.';
        box.style.cssText = 'color:#55677f;margin-bottom:6px';
      } else {
        const head = document.createElement('div');
        head.style.cssText = 'color:#9fb4cc;margin-bottom:6px;font:11px system-ui';
        const d = view.distanceM >= 1000 ? `${(view.distanceM / 1000).toFixed(1)} km` : `${Math.round(view.distanceM)} m`;
        const turns = boardTurnsInS(Date.now());
        head.textContent = `${view.dealerName} keeps the schematic stall at ${view.settlementName} (${view.site.tier}) — ${d} away${view.inRange ? ', in range' : ' · walk within 400 m of the square to buy'}. The board turns in ${Math.floor(turns / 3600)} h ${Math.floor((turns % 3600) / 60)} min.`;
        box.appendChild(head);
        for (const o of view.offers) {
          const r = this.row();
          r.className = 'dealer-offer';
          r.dataset.key = o.schematicKey;
          r.appendChild(this.icon(o.schematicKey, '📐'));
          const held = this.knows(o.schematicKey);
          r.appendChild(this.infoCell(`${o.name} schematic`, `${o.priceSov} ⓒ`, `complexity ${o.complexity}${held ? ' · already held' : ''}`));
          const buy = this.btn(held ? 'Held' : 'Buy', () => {
            conn.reducers.buySchematic({ settlementId: view.settlementId, schematicKey: o.schematicKey })
              .then(() => this.note(`${o.name} schematic learned — ${view.dealerName} pockets the credits`, false, 'sfx_coin_purchase'))
              .catch((e: Error) => this.note(e.message, true));
          });
          if (held || !view.inRange) { buy.disabled = true; buy.style.opacity = '0.4'; }
          r.appendChild(buy);
          box.appendChild(r);
        }
      }
      body.appendChild(box);
    }
    this.section(body, 'MY STALLS', 8);
    for (const l of mine) {
      const r = this.row();
      r.appendChild(this.icon(l.kind === 'resource' ? l.familyKey : l.itemKey, '🪙'));
      r.appendChild(this.infoCell(`${l.name}${l.kind === 'resource' ? ` × ${l.qty}` : ''}`, `${l.priceSov} ⓒ`,
        l.kind === 'resource' ? this.statLine(l.statsJson) : `q ${l.quality} · ${l.serial}`));
      r.appendChild(this.btn('Cancel', () => {
        conn.reducers.cancelListing({ listingId: l.listingId })
          .then(() => this.note('Listing returned to your hold'))
          .catch((e: Error) => this.note(e.message, true));
      }));
      body.appendChild(r);
    }
    this.section(body, 'YOUR PACKED DEEDS — list them here', 8);
    {
      const deeds = [...conn.db.structureDeed.iter()].filter((d) => d.owner.isEqual(me) && d.packed && !d.noTrade);
      for (const d of deeds.slice(0, 8)) {
        const r = this.row();
        r.appendChild(this.icon(d.structureType, '📜'));
        r.appendChild(this.infoCell(`${d.structureType.replace(/_/g, ' ')} deed`, `${d.lotsRequired} lots`, `maint ${d.baseMaintPerHour}/h`));
        r.appendChild(this.btn('List…', () => {
          const p2 = window.prompt('Asking price (credits)', '500');
          const price = p2 ? BigInt(Math.max(1, Math.floor(Number(p2)))) : 0n;
          if (price > 0n) {
            conn.reducers.listDeed({ deedId: d.deedId, priceSov: price })
              .then(() => this.note('Deed listed on the bazaar'))
              .catch((e: Error) => this.note(e.message, true));
          }
        }));
        body.appendChild(r);
      }
      if (deeds.length === 0) {
        const none = document.createElement('div');
        none.textContent = 'No packed deeds in your hold.';
        none.style.cssText = 'color:#55677f;margin-bottom:4px';
        body.appendChild(none);
      }
    }
    this.section(body, 'VERSONA STALLS — the merchants of the towns', 8);
    {
      const stalls = [...conn.db.versonaListing.iter()].filter((l) => l.active && l.qty > 0)
        .sort((a, b) => Number(a.priceSov - b.priceSov)).slice(0, 24);
      for (const l of stalls) {
        const agent = conn.db.npcAgent.versonaId.find(l.versonaId);
        const r = this.row();
        r.appendChild(this.icon(l.good, '🧺'));
        r.appendChild(this.infoCell(`${l.name} × ${l.qty}`, `${l.priceSov} ⓒ each`,
          `merchant #${l.versonaId} of settlement ${l.settlementId}${agent ? ` · purse ${agent.wallet}` : ''}`));
        // Merchant slice (2026-09-15): face to face the Buy runs the COUNTER
        // protocol (approach → hold → settle, one request id per attempt); from
        // afar it is the bazaar terminal's direct buy, which sees only the
        // units nobody at the counter is holding.
        const svc = conn.db.merchantService.versonaId.find(l.versonaId);
        const meId = conn.identity;
        const counter = svc && svc.phase !== 'idle'
          ? (meId && svc.customer.isEqual(meId) ? (svc.phase === 'handover' ? 'handing you the goods' : 'serving you') : 'serving another customer')
          : '';
        if (counter) r.appendChild(this.infoCell('', counter, ''));
        r.appendChild(this.btn('Buy 1', () => { void this.buyAtCounter(conn, l); }));
        body.appendChild(r);
      }
      if (stalls.length === 0) {
        const none = document.createElement('div');
        none.textContent = 'No merchant stalls right now — they restock at the market through the day.';
        none.style.cssText = 'color:#55677f;margin-bottom:4px';
        body.appendChild(none);
      }
    }
    this.section(body, 'THE BAZAAR', 8);
    for (const l of others.sort((a, b) => Number(a.priceSov - b.priceSov)).slice(0, 30)) {
      const r = this.row();
      r.appendChild(this.icon(l.kind === 'resource' ? l.familyKey : l.itemKey, '🪙'));
      r.appendChild(this.infoCell(`${l.name}${l.kind === 'resource' ? ` × ${l.qty}` : ''}`, `${l.priceSov} ⓒ`,
        l.kind === 'resource' ? this.statLine(l.statsJson) : `q ${l.quality} · ${l.serial}`));
      r.appendChild(this.btn('Buy', () => {
        conn.reducers.buyListing2({ listingId: l.listingId })
          .then(() => this.note(`Bought ${l.name}`, false, 'sfx_coin_purchase'))
          .catch((e: Error) => this.note(e.message, true));
      }));
      body.appendChild(r);
    }
    if (others.length === 0 && mine.length === 0) {
      const empty = document.createElement('div');
      empty.textContent = 'The bazaar stands quiet — list resources or items from the Inventory tab.';
      body.appendChild(empty);
    }
  }
}
